import { VoucherResponse } from '@/app/types/voucher'

// Extended VoucherResponse interface with optional status fields
export interface ExtendedVoucherResponse extends VoucherResponse {
  status?: string;
  payment_status?: string;
}

export type SimplifiedSalesInvoiceStatus =
  | 'Unpaid'
  | 'Partially Paid'
  | 'Paid'
  | 'Cancelled'
  | 'Unknown'

export const getSimplifiedSalesInvoiceStatus = (
  salesInvoice: ExtendedVoucherResponse | null | undefined
): SimplifiedSalesInvoiceStatus => {
  if (!salesInvoice) {
    return 'Unknown'
  }

  if (salesInvoice.is_cancelled) {
    return 'Cancelled'
  }

  // Check payment_status for Draft invoices to determine if partially paid
  if (salesInvoice.status === 'Draft' && salesInvoice.payment_status === 'Partially Paid') {
    return 'Partially Paid'
  }

  // Map status field values to simplified display status
  switch (salesInvoice.status) {
    case 'Draft':
      return 'Unpaid'
    case 'Posted':
      return 'Paid'
    case 'Paid':
      return 'Paid'
    case 'Partially Paid':
      return 'Partially Paid'
    default:
      // Fallback: if status is not set, consider it Unpaid
      if (salesInvoice.total_amount && salesInvoice.total_amount > 0) {
        return 'Unpaid'
      }
      return 'Unknown'
  }
}

// Reverse mapping to get backend filter values from simplified status
export const getBackendStatusFilters = (simplifiedStatus: SimplifiedSalesInvoiceStatus): { field: string; value: string | number | boolean | string[]; op: string }[] => {
  switch (simplifiedStatus) {
    case 'Cancelled':
      return [{ field: 'is_cancelled', value: true, op: 'eq' }];
    
    case 'Paid':
      // For Paid, filter by status in ['Posted', 'Paid'] using the 'in' operator
      return [{ field: 'status', value: ['Posted', 'Paid'], op: 'in' }];
    
    case 'Partially Paid':
      // For Partially Paid, filter by payment_status = 'Partially Paid'
      return [{ field: 'payment_status', value: 'Partially Paid', op: 'eq' }];
    
    case 'Unpaid':
      // For Unpaid, filter by status = 'Draft' AND payment_status = 'Unpaid' (or null)
      return [
        { field: 'status', value: 'Draft', op: 'eq' },
        { field: 'payment_status', value: 'Unpaid', op: 'eq' }
      ];
    
    default:
      return [];
  }
}
